import React from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import DashboardHeader from "./DashboardHeader";
import type { DashboardPageProps } from "./dashboard.types";
import SectionCard from "../../components/dashlibs/SectionCard";
import SummaryCard, {
  type SummaryTone,
} from "../../components/dashlibs/SummaryCard";
import { AreaTrendChart, BarChart } from "../../components/charts";

type DashboardKind = "executive" | "sales" | "stock";

type Props = DashboardPageProps & {
  dashboard: DashboardKind;
  title: string;
  subtitle: string;
};

type TableColumn = {
  key: string;
  label: string;
  align?: "left" | "right";
  format?: "currency" | "number" | "status";
};

const iconMap: Record<string, React.ReactNode> = {
  point_of_sale: <PointOfSaleOutlinedIcon />,
  account_balance_wallet: <AccountBalanceWalletOutlinedIcon />,
  approval: <ApprovalOutlinedIcon />,
  inventory: <Inventory2OutlinedIcon />,
  inventory_2: <Inventory2OutlinedIcon />,
  warning: <WarningAmberOutlinedIcon />,
  production_quantity_limits: <WarningAmberOutlinedIcon />,
  request_quote: <RequestQuoteOutlinedIcon />,
  shopping_cart: <ShoppingCartOutlinedIcon />,
  payments: <PaymentsOutlinedIcon />,
  support_agent: <SupportAgentOutlinedIcon />,
  track_changes: <TrackChangesOutlinedIcon />,
  filter_alt: <FilterAltOutlinedIcon />,
  trending_up: <TrendingUpOutlinedIcon />,
  tune: <TuneOutlinedIcon />,
};

const asNumber = (value: any) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value: any, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("en-GH", {
    maximumFractionDigits,
  }).format(asNumber(value));

const formatCurrency = (value: any, currencyCode = "GHS") =>
  new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(asNumber(value));

const formatCardValue = (card: any, currencyCode: string) => {
  if (card?.format === "currency") {
    return formatCurrency(card.value, card.currencyCode || currencyCode);
  }
  if (card?.format === "percent") {
    return `${formatNumber(card.value)}%`;
  }
  return formatNumber(card?.value);
};

const cardSubtitle = (card: any, currencyCode: string) => {
  if (card?.overdueCount !== undefined) {
    return `${formatNumber(card.overdueCount, 0)} overdue`;
  }
  if (card?.pendingCount !== undefined) {
    return `${formatNumber(card.pendingCount, 0)} pending`;
  }
  if (card?.count !== undefined) {
    return `${formatNumber(card.count, 0)} records`;
  }
  if (card?.amount !== undefined) {
    return formatCurrency(card.amount, currencyCode);
  }
  if (card?.targetAmount !== undefined) {
    return `${formatCurrency(card.actualAmount, currencyCode)} of ${formatCurrency(
      card.targetAmount,
      currencyCode
    )}`;
  }
  if (card?.quantity !== undefined) {
    return `${formatNumber(card.quantity)} units`;
  }
  return undefined;
};

function DataTable({
  rows,
  columns,
  currencyCode,
}: {
  rows: any[];
  columns: TableColumn[];
  currencyCode: string;
}) {
  if (!rows.length) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", color: "#66788A" }}>
        No records for the selected filters.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{ fontWeight: 800, color: "#17324D", whiteSpace: "nowrap" }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow hover key={row.documentId || row.followupId || row.movementId || index}>
              {columns.map((column) => {
                const value = row[column.key];
                let content: React.ReactNode = value ?? "—";

                if (column.format === "currency") {
                  content = formatCurrency(value, currencyCode);
                } else if (column.format === "number") {
                  content = formatNumber(value);
                } else if (column.format === "status") {
                  content = (
                    <Chip
                      size="small"
                      label={value || "Unknown"}
                      sx={{ fontWeight: 700, bgcolor: "#EEF5F8", color: "#17324D" }}
                    />
                  );
                }

                return (
                  <TableCell key={column.key} align={column.align}>
                    {content}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const barData = (rows: any[], labelKey: string, valueKey: string) =>
  rows.map((row) => ({
    label: String(row[labelKey] || "Unknown"),
    value: asNumber(row[valueKey]),
    color: "#176B5B",
  }));

export default function SCSMSDashboard({
  dashboard,
  title,
  subtitle,
  userData,
  dashData,
  sharedFilters,
  filterOptions,
  onFilterChange,
  onRefresh,
  onExport,
}: Props) {
  const { meta = {}, sections = {}, summaryCards = [] } = dashData || {};
  const currencyCode = meta.currencyCode || "GHS";

  const summary = (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(6, minmax(0, 1fr))",
        },
      }}
    >
      {summaryCards.map((card: any) => (
        <SummaryCard
          key={card.code}
          title={card.label}
          value={formatCardValue(card, currencyCode)}
          subtitle={cardSubtitle(card, currencyCode)}
          tone={(card.tone || "default") as SummaryTone}
          icon={iconMap[card.icon] || <InsightsOutlinedIcon />}
        />
      ))}
    </Box>
  );

  const executiveSections = (
    <>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.4fr) minmax(0, 1fr)" } }}>
        <SectionCard title={sections.grossSalesTrend?.title || "Gross sales trend"}>
          <AreaTrendChart
            data={(sections.grossSalesTrend?.data || []).map((row: any) => ({
              label: row.label,
              value: asNumber(row.netSales),
            }))}
          />
        </SectionCard>
        <SectionCard title={sections.salesByLocation?.title || "Sales by location"}>
          <BarChart
            horizontal
            data={barData(sections.salesByLocation?.data || [], "locationName", "sales")}
          />
        </SectionCard>
      </Box>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(3, minmax(0, 1fr))" } }}>
        <SectionCard title={sections.topCustomers?.title || "Top customers"}>
          <BarChart
            horizontal
            data={barData(sections.topCustomers?.data || [], "customerName", "sales")}
          />
        </SectionCard>
        <SectionCard title={sections.topProducts?.title || "Top products"}>
          <BarChart
            horizontal
            data={barData(sections.topProducts?.data || [], "productName", "sales")}
          />
        </SectionCard>
        <SectionCard title={sections.cuttingWorkload?.title || "Cutting-service workload"}>
          <BarChart
            horizontal
            data={barData(sections.cuttingWorkload?.data || [], "statusName", "quantity")}
          />
        </SectionCard>
      </Box>
    </>
  );

  const salesSections = (
    <>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.4fr) minmax(0, 1fr)" } }}>
        <SectionCard title={sections.salesTrend?.title || "Sales trend"}>
          <AreaTrendChart
            data={(sections.salesTrend?.data || []).map((row: any) => ({
              label: row.date,
              value: asNumber(row.sales),
            }))}
          />
        </SectionCard>
        <SectionCard title={sections.pipelineByStatus?.title || "Pipeline by status"}>
          <BarChart
            horizontal
            data={barData(sections.pipelineByStatus?.data || [], "statusName", "value")}
          />
        </SectionCard>
      </Box>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.35fr) minmax(0, 1fr)" } }}>
        <SectionCard title={sections.pipelineDocuments?.title || "Open pipeline"}>
          <DataTable
            rows={sections.pipelineDocuments?.data || []}
            currencyCode={currencyCode}
            columns={[
              { key: "documentNo", label: "Document" },
              { key: "customerName", label: "Customer" },
              { key: "salespersonName", label: "Salesperson" },
              { key: "statusName", label: "Status", format: "status" },
              { key: "expectedDate", label: "Expected" },
              { key: "value", label: "Value", format: "currency", align: "right" },
            ]}
          />
        </SectionCard>
        <SectionCard title={sections.salesTargets?.title || "Sales targets"}>
          <BarChart
            horizontal
            data={barData(sections.salesTargets?.data || [], "salespersonName", "target")}
          />
        </SectionCard>
      </Box>
      <SectionCard title={sections.customerFollowups?.title || "Customer follow-ups"}>
        <DataTable
          rows={sections.customerFollowups?.data || []}
          currencyCode={currencyCode}
          columns={[
            { key: "followupDate", label: "Follow-up date" },
            { key: "customerName", label: "Customer" },
            { key: "subject", label: "Subject" },
            { key: "statusCode", label: "Status", format: "status" },
          ]}
        />
      </SectionCard>
    </>
  );

  const stockSections = (
    <>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.35fr) minmax(0, 1fr)" } }}>
        <SectionCard title={sections.balanceByLocation?.title || "Stock balance by location"}>
          <BarChart
            horizontal
            data={barData(
              sections.balanceByLocation?.data || [],
              "locationName",
              "availableQuantity"
            )}
          />
        </SectionCard>
        <SectionCard title={sections.stockAging?.title || "Stock aging"}>
          <BarChart
            data={barData(sections.stockAging?.data || [], "bucket", "stockValue")}
          />
        </SectionCard>
      </Box>
      <Box sx={{ display: "grid", gap: 2, minWidth: 0, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" } }}>
        <SectionCard title={sections.lowStockItems?.title || "Low-stock items"}>
          <BarChart
            horizontal
            data={barData(sections.lowStockItems?.data || [], "productName", "shortfall")}
          />
        </SectionCard>
        <SectionCard title={sections.fastMovingItems?.title || "Fast-moving items"}>
          <BarChart
            horizontal
            data={barData(
              sections.fastMovingItems?.data || [],
              "productName",
              "quantityMoved"
            )}
          />
        </SectionCard>
      </Box>
      <SectionCard title={sections.stockAdjustments?.title || "Stock adjustments"}>
        <DataTable
          rows={sections.stockAdjustments?.data || []}
          currencyCode={currencyCode}
          columns={[
            { key: "movementDate", label: "Date" },
            { key: "movementTypeCode", label: "Type" },
            { key: "productName", label: "Product" },
            { key: "variantName", label: "Variant" },
            { key: "locationName", label: "Location" },
            { key: "quantity", label: "Quantity", format: "number", align: "right" },
            { key: "approvalStatusCode", label: "Status", format: "status" },
          ]}
        />
      </SectionCard>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2.5, width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}>
      <DashboardHeader
        title={title}
        subtitle={subtitle}
        userData={userData}
        sharedFilters={sharedFilters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onRefresh={onRefresh}
        onExport={() => {
          void onExport?.(dashboard);
        }}
      />
      {summary}
      {dashboard === "executive" && executiveSections}
      {dashboard === "sales" && salesSections}
      {dashboard === "stock" && stockSections}
    </Box>
  );
}
